import React, { useState } from 'react';
import { Save, Calculator, DollarSign, AlertCircle, RefreshCw, ArrowLeft, Coins } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export default function ArqueoCaja() {
    const navigate = useNavigate();
    const [denominaciones, setDenominaciones] = useState({
        // Billetes
        '100000': '',
        '50000': '',
        '20000': '',
        '10000': '',
        '5000': '',
        '2000': '',
        // Monedas
        '1000': '',
        '500': '',
        '100': '',
        '50': ''
    });

    const [saldoSistema, setSaldoSistema] = useState(15450000); // Mock value
    const [observacion, setObservacion] = useState('');
    const [loading, setLoading] = useState(false);

    const calcularTotalFisico = () => {
        return Object.entries(denominaciones).reduce((acc, [valor, cantidad]) => {
            return acc + (parseInt(valor) * (parseInt(cantidad) || 0));
        }, 0);
    };

    const totalFisico = calcularTotalFisico();
    const diferencia = totalFisico - saldoSistema;

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
            setDenominaciones(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleCierre = async () => {
        if (totalFisico === 0 && !observacion) {
            toast.error('Por favor, ingrese el conteo físico o una observación.');
            return;
        }

        try {
            setLoading(true);
            // Simular API Call
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            toast.success('Arqueo de cierre registrado exitosamente');
            // navigate('/algun-lado');
        } catch (error) {
            toast.error('Error al registrar el arqueo');
        } finally {
            setLoading(false);
        }
    };

    const formatGua = (val) => new Intl.NumberFormat('es-PY').format(val);

    const billetes = ['100000', '50000', '20000', '10000', '5000', '2000'];
    const monedas = ['1000', '500', '100', '50'];

    const renderDenominacionRow = (valor) => (
        <div key={valor} className="flex items-center gap-4 p-3 bg-white border border-gray-100 rounded-lg hover:border-blue-200 hover:bg-slate-50 transition-all group">
            <div className="flex-1">
                <div className="text-[10px] font-bold text-gray-400 uppercase leading-none mb-1">Denominación</div>
                <div className="font-bold text-gray-700 text-base">{formatGua(valor)} <span className="text-[10px] text-gray-400">Gs.</span></div>
            </div>
            
            <div className="w-24">
                <div className="text-[10px] font-bold text-gray-400 uppercase leading-none mb-1 text-center font-mono">CANTIDAD</div>
                <input
                    type="text"
                    name={valor}
                    inputMode="numeric"
                    value={denominaciones[valor]}
                    onChange={handleChange}
                    placeholder="0"
                    className="w-full py-2 px-2 text-center text-lg font-bold border-2 border-gray-200 rounded focus:border-blue-500 focus:ring-4 focus:ring-blue-50 focus:outline-none transition-all placeholder-gray-300"
                />
            </div>

            <div className="w-32 text-right">
                <div className="text-[10px] font-bold text-gray-400 uppercase leading-none mb-1">SUBTOTAL</div>
                <div className="font-bold text-blue-700 text-sm">
                    {formatGua(parseInt(valor) * (parseInt(denominaciones[valor]) || 0))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto bg-gray-50/50 min-h-screen pb-12">
            <Toaster position="top-right" />

            {/* Header Standard Enterprise */}
            <div className="bg-slate-700 p-4 flex items-center justify-between text-white shadow-md sticky top-0 z-10">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-1.5 hover:bg-slate-600 rounded-lg transition-colors text-slate-300 hover:text-white"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="bg-slate-600 p-2 rounded-lg">
                            <Calculator className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight">ARQUEO DE CIERRE DE CAJA</h1>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Tesorería & Operaciones</p>
                        </div>
                    </div>
                </div>
                
                <div className="flex items-center gap-4 bg-slate-800/50 px-4 py-2 rounded-xl border border-slate-600">
                    <div className="text-right">
                        <p className="text-[9px] font-bold text-slate-400 uppercase leading-none">Total Arqueado</p>
                        <p className="text-xl font-black text-blue-400 leading-none mt-1">{formatGua(totalFisico)} <span className="text-xs">Gs.</span></p>
                    </div>
                </div>
            </div>

            <div className="p-6 grid grid-cols-1 xl:grid-cols-12 gap-6">
                
                {/* Panel Central Conteo */}
                <div className="xl:col-span-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Billetes */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                            <div className="bg-slate-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                <h2 className="text-xs font-black text-gray-600 uppercase tracking-widest flex items-center gap-2">
                                    <DollarSign className="w-4 h-4 text-green-600" /> BILLETES
                                </h2>
                                <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">EFECTIVO</span>
                            </div>
                            <div className="p-4 space-y-3">
                                {billetes.map(renderDenominacionRow)}
                            </div>
                        </div>

                        {/* Monedas */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-fit">
                            <div className="bg-slate-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                <h2 className="text-xs font-black text-gray-600 uppercase tracking-widest flex items-center gap-2">
                                    <Coins className="w-4 h-4 text-amber-600" /> MONEDAS
                                </h2>
                                <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">MENUDO</span>
                            </div>
                            <div className="p-4 space-y-3">
                                {monedas.map(renderDenominacionRow)}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Sidebar Resumen */}
                <div className="xl:col-span-4 space-y-6">
                    
                    {/* Tarjeta de Resumen Financiero */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="bg-slate-700 px-4 py-3 text-white">
                            <h2 className="text-xs font-black uppercase tracking-widest">Resumen de Cuadre</h2>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="flex justify-between items-end border-b border-gray-100 pb-3">
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase">Saldo en Sistema</p>
                                    <p className="text-lg font-bold text-gray-700">{formatGua(saldoSistema)} Gs.</p>
                                </div>
                                <div className="text-right space-y-0.5">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase">Diferencia</p>
                                    <p className={`text-lg font-black ${diferencia === 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {diferencia > 0 ? '+' : ''}{formatGua(diferencia)} Gs.
                                    </p>
                                </div>
                            </div>

                            {diferencia !== 0 && (
                                <div className="bg-red-50 border border-red-100 p-3 rounded-lg flex gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                                    <p className="text-xs text-red-700 font-medium">
                                        Existe un descuadre en caja. Por favor verifique el conteo o indique el motivo en observaciones.
                                    </p>
                                </div>
                            )}

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2">Observaciones del Cierre</label>
                                <textarea
                                    className="w-full border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-50 focus:border-blue-400 outline-none transition-all border-2 min-h-[100px]"
                                    placeholder="Detalle cualquier novedad..."
                                    value={observacion}
                                    onChange={(e) => setObservacion(e.target.value)}
                                ></textarea>
                            </div>

                            <button
                                onClick={handleCierre}
                                disabled={loading}
                                className="w-full bg-blue-600 text-white py-4 rounded-xl font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-200 flex items-center justify-center gap-3 disabled:opacity-70 group"
                            >
                                {loading ? (
                                    <RefreshCw className="w-5 h-5 animate-spin" />
                                ) : (
                                    <Save className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                )}
                                {loading ? 'PROCESANDO...' : 'CONFIRMAR Y CERRAR CAJA'}
                            </button>
                        </div>
                    </div>

                    {/* Tip Informativo */}
                    <div className="bg-blue-600 rounded-xl p-5 text-white shadow-xl shadow-blue-100 relative overflow-hidden group">
                        <DollarSign className="absolute -right-4 -bottom-4 w-24 h-24 text-blue-500 opacity-30 rotate-12 group-hover:scale-110 transition-transform" />
                        <h3 className="font-bold text-sm mb-2 relative z-10">¿Sabías que?</h3>
                        <p className="text-[11px] text-blue-100 leading-relaxed relative z-10">
                            Un arqueo preciso garantiza la integridad de tu turno. Asegúrate de contar por separado cada fajo de billetes antes de ingresar el total.
                        </p>
                    </div>

                </div>
            </div>
        </div>
    );
}
